import { datadogRum } from '@datadog/browser-rum';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import useAuth from '@/hooks/useAuth';

import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import Constants from '@/constants';

import { queryClient } from '@/services';
import { IntlCategoryDTO } from '@/services/content-api-types';

import { getAdminState } from './state';

import {
  getCategoryByCountryCodeQueryKey,
  saveCategory,
} from '@/_generated/api-clients/content';
import { getCategoryQueryKey } from '@/_generated/api-clients/xp';

export const useIsAdmin = () => {
  const { data } = useAuth();

  return Constants.USER_PERMISSIONS.ADMIN === data?.highestPermissionLevel;
};

export const useEditCategory = () => {
  const { selectedCountry } = getAdminState();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: IntlCategoryDTO;
      categoryName?: string;
      onSuccess?: () => void;
    }) => saveCategory({ body: payload }),
    onSuccess: (_data, { payload, categoryName, onSuccess }) => {
      queryClient.invalidateQueries({
        queryKey: getCategoryByCountryCodeQueryKey({
          query: { country: selectedCountry.label, isAdmin: true },
        }),
      });

      if (payload.id) {
        queryClient.invalidateQueries({
          queryKey: getCategoryQueryKey({
            query: { country: selectedCountry.label },
            path: { id: payload.id },
          }),
        });
      }

      queryClient.invalidateQueries({
        queryKey: ['browse'],
      });

      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {t('Admin.updateCategoryToast', { categoryName })}
        </ToastMessageBlock>
      ));
      onSuccess?.();
    },
    onError: err => {
      console.error(err);
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id} severity="error">
          {Constants.IS_IN_CYPRESS_TEST_LOCAL || Constants.IS_IN_JEST_TEST
            ? 'UpdateCategory Fail In Cypress Test'
            : err.message}
        </ToastMessageBlock>
      ));
      datadogRum.addError(err);
    },
  });
};
